import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { environment } from '../../../../environments/environment';
import { restoreCsrTheme } from '../../csr/csr-theme';

type InteractionStatus = 'PENDING_CSR' | 'ASSIGNED' | 'CSR_REPLIED' | 'CLOSED';

interface OmnichannelInteraction {
  id: number;
  source: string;
  externalMessageId: string;
  externalConversationId: string | null;
  externalUserId: string | null;
  citizenName: string | null;
  citizenHandle: string | null;
  citizenPhone: string | null;
  messageText: string;
  botAnswer: string | null;
  botConfidence: number | null;
  handoffReason: string | null;
  priority: string | null;
  assignedQueue: string | null;
  assignedAgentUsername: string | null;
  status: InteractionStatus;
  replyText: string | null;
  replyBy: string | null;
  repliedAt: string | null;
  deliveryStatus: string | null;
  deliveredAt: string | null;
  lastError: string | null;
  createdAt: string;
}

interface InboxStats {
  total: number;
  pending: number;
  assigned: number;
  replied: number;
  closed: number;
  facebook: number;
  whatsapp: number;
}

@Component({
  selector: 'app-omnichannel-inbox',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './omnichannel-inbox.component.html',
  styleUrls: ['./omnichannel-inbox.component.scss'],
})
export class OmnichannelInboxComponent implements OnInit, OnDestroy {
  items: OmnichannelInteraction[] = [];
  stats: InboxStats = { total: 0, pending: 0, assigned: 0, replied: 0, closed: 0, facebook: 0, whatsapp: 0 };

  selected: OmnichannelInteraction | null = null;
  replyDraft = '';
  filterSource = '';
  filterStatus = '';
  filterQ = '';

  loading = false;
  saving = false;
  errorMessage = '';
  successMessage = '';
  currentPage = 0;
  totalElements = 0;
  totalPages = 0;
  readonly pageSize = 20;
  private refreshInterval: any = null;

  ngOnInit(): void {
    restoreCsrTheme();
    void this.refresh();
    this.refreshInterval = setInterval(() => void this.refresh(false), 30000);
  }

  ngOnDestroy(): void {
    if (this.refreshInterval) clearInterval(this.refreshInterval);
  }

  get displayName(): string {
    return this.selected?.citizenName || this.selected?.citizenHandle || this.selected?.citizenPhone || 'Unknown citizen';
  }

  async refresh(showSpinner = true): Promise<void> {
    await Promise.all([this.loadStats(), this.loadItems(showSpinner)]);
  }

  applyFilters(): void {
    this.currentPage = 0;
    void this.loadItems();
  }

  clearFilters(): void {
    this.filterSource = '';
    this.filterStatus = '';
    this.filterQ = '';
    this.currentPage = 0;
    void this.loadItems();
  }

  select(item: OmnichannelInteraction): void {
    this.selected = item;
    this.replyDraft = item.replyText || '';
  }

  async assignToMe(item: OmnichannelInteraction): Promise<void> {
    if (!this.canAssign(item)) return;
    this.saving = true;
    this.clearMessages();
    try {
      const json = await this.requestJson(`${environment.apiHost}/api/v1/omnichannel/interactions/${item.id}/assign`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({}),
      });
      this.successMessage = json?.message || 'Interaction assigned.';
      await this.refresh(false);
      this.reselect(item.id);
    } catch (error) {
      this.errorMessage = this.errorText(error);
    } finally {
      this.saving = false;
    }
  }

  async sendReply(): Promise<void> {
    if (!this.selected || !this.replyDraft.trim() || !this.canReply(this.selected)) return;
    this.saving = true;
    this.clearMessages();
    try {
      const selectedId = this.selected.id;
      const json = await this.requestJson(`${environment.apiHost}/api/v1/omnichannel/interactions/${selectedId}/reply`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify({ replyText: this.replyDraft.trim() }),
      });
      this.successMessage = json?.message || 'Reply queued for delivery.';
      await this.refresh(false);
      this.reselect(selectedId);
    } catch (error) {
      this.errorMessage = this.errorText(error);
    } finally {
      this.saving = false;
    }
  }

  async closeSelected(): Promise<void> {
    if (!this.selected || !this.canClose(this.selected)) return;
    this.saving = true;
    this.clearMessages();
    try {
      const json = await this.requestJson(`${environment.apiHost}/api/v1/omnichannel/interactions/${this.selected.id}/close`, {
        method: 'POST',
        headers: this.authHeaders(),
      });
      this.successMessage = json?.message || 'Interaction closed.';
      this.selected = null;
      this.replyDraft = '';
      await this.refresh(false);
    } catch (error) {
      this.errorMessage = this.errorText(error);
    } finally {
      this.saving = false;
    }
  }

  goToPrev(): void {
    if (this.currentPage > 0) {
      this.currentPage--;
      void this.loadItems();
    }
  }

  goToNext(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.currentPage++;
      void this.loadItems();
    }
  }

  sourceLabel(source: string): string {
    const labels: Record<string, string> = {
      FACEBOOK: 'Facebook',
      WHATSAPP: 'WhatsApp',
      INSTAGRAM: 'Instagram',
      YOUTUBE: 'YouTube',
      X: 'X',
      LINKEDIN: 'LinkedIn',
      WEB_CHAT: 'Web Chat',
      SMS: 'SMS',
      EMAIL: 'Email',
      IVR: 'IVR',
    };
    return labels[source] || source;
  }

  statusLabel(status: string): string {
    const labels: Record<string, string> = {
      PENDING_CSR: 'Pending',
      ASSIGNED: 'Assigned',
      CSR_REPLIED: 'Reply queued',
      CLOSED: 'Closed',
    };
    return labels[status] || status;
  }

  deliveryLabel(status: string | null, item?: OmnichannelInteraction | null): string {
    if (status === 'PENDING' && !item?.replyText) return 'Waiting for reply';
    const labels: Record<string, string> = {
      PENDING: 'Delivery pending',
      SENT: 'Sent to Facebook',
      DELIVERED: 'Delivered',
      FAILED: 'Delivery failed',
    };
    return status ? (labels[status] || status) : '-';
  }

  canAssign(item: OmnichannelInteraction | null): boolean {
    return !!item && item.status === 'PENDING_CSR' && !this.saving;
  }

  canReply(item: OmnichannelInteraction | null): boolean {
    if (!item || this.saving) return false;
    const alreadyDelivered = item.deliveryStatus === 'SENT' || item.deliveryStatus === 'DELIVERED';
    return (item.status === 'ASSIGNED' && !alreadyDelivered)
      || (item.status === 'CSR_REPLIED' && item.deliveryStatus === 'FAILED');
  }

  canClose(item: OmnichannelInteraction | null): boolean {
    if (!item || this.saving || item.status === 'CLOSED') return false;
    return !(item.status === 'CSR_REPLIED' && item.deliveryStatus === 'PENDING');
  }

  replyButtonLabel(item: OmnichannelInteraction | null): string {
    return item?.deliveryStatus === 'FAILED' ? 'Retry reply' : 'Queue reply';
  }

  confidence(score: number | null): string {
    return typeof score === 'number' ? `${Math.round(score * 100)}%` : '-';
  }

  formatDate(iso: string | null): string {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString([], { day: '2-digit', month: 'short' }) +
      ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  private async loadStats(): Promise<void> {
    try {
      const json: any = await this.requestJson(`${environment.apiHost}/api/v1/omnichannel/stats`, {
        headers: this.authHeaders(),
      });
      this.stats = json?.data ?? json;
    } catch (error) {
      this.errorMessage = this.errorText(error);
    }
  }

  private async loadItems(showSpinner = true): Promise<void> {
    if (showSpinner) this.loading = true;
    this.clearMessages();
    const body: any = { page: this.currentPage, size: this.pageSize };
    if (this.filterSource) body.source = this.filterSource;
    if (this.filterStatus) body.status = this.filterStatus;
    if (this.filterQ.trim()) body.q = this.filterQ.trim();

    try {
      const json: any = await this.requestJson(`${environment.apiHost}/api/v1/omnichannel/interactions`, {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify(body),
      });
      const payload = json?.data ?? json;
      this.items = payload?.items ?? [];
      this.totalElements = payload?.totalElements ?? 0;
      this.totalPages = payload?.totalPages ?? 0;
      if (!this.selected && this.items.length) this.select(this.items[0]);
      if (this.selected && !this.items.some(i => i.id === this.selected?.id)) {
        this.selected = this.items[0] ?? null;
        this.replyDraft = this.selected?.replyText ?? '';
      }
    } catch (error) {
      this.items = [];
      this.totalElements = 0;
      this.totalPages = 0;
      this.selected = null;
      this.replyDraft = '';
      this.errorMessage = this.errorText(error);
    } finally {
      this.loading = false;
    }
  }

  private reselect(id: number): void {
    const updated = this.items.find(i => i.id === id);
    if (updated) this.select(updated);
  }

  private clearMessages(): void {
    this.errorMessage = '';
    this.successMessage = '';
  }

  private async requestJson(url: string, init: RequestInit): Promise<any> {
    const res = await fetch(url, init);
    const text = await res.text();
    let json: any = null;
    if (text) {
      try {
        json = JSON.parse(text);
      } catch {
        json = { message: text };
      }
    }
    if (!res.ok) {
      throw new Error(json?.message || `Request failed with HTTP ${res.status}`);
    }
    return json;
  }

  private errorText(error: unknown): string {
    return error instanceof Error ? error.message : 'Request failed. Please try again.';
  }

  private authHeaders(): Record<string, string> {
    const token = localStorage.getItem('auth-token') ?? '';
    return token
      ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', Accept: 'application/json' }
      : { 'Content-Type': 'application/json', Accept: 'application/json' };
  }
}
